import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  output,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

import { DatasetFilterInfo, DatasetFilterScope } from '@blaze-shared/api-contracts-domain';
import { DialogService } from '@blaze-shared/shared-feature';
import {
  ElementLoadingIndicatorComponent,
  ScrollerComponent,
  TooltipDirective,
} from '@blaze-shared/shared-ui';
import { IconsModule } from '@blaze-shared/theming-data-access';

export type SavedFiltersUIMode = 'list' | 'form';

@Component({
  selector: 'blaze-saved-filters',
  templateUrl: './saved-filters.component.html',
  styleUrls: ['./saved-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TooltipDirective,
    MatSlideToggleModule,
    IconsModule,
    ElementLoadingIndicatorComponent,
    ScrollerComponent,
  ],
})
export class SavedFiltersComponent {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialog = inject(DialogService);

  apply = output<DatasetFilterInfo>();
  delete = output<DatasetFilterInfo>();
  save = output<{
    name: string;
    scope: DatasetFilterScope;
  }>();

  @Input() allowToCreatePublicFilters = false;
  @Input() disableSave = false;
  @Input() filters: DatasetFilterInfo[] = [];
  @Input() mode: SavedFiltersUIMode = 'list';
  @Input() showLoadingIndicator = false;

  form = new FormGroup({
    name: new FormControl<string>('', [Validators.required]),
    public: new FormControl<boolean>(false),
  });

  readonly DatasetFilterScope = DatasetFilterScope;
  readonly trackByNameAndScope = (_index: number, filter: DatasetFilterInfo): string =>
    `${filter.name}${filter.scope}`;

  changeMode(mode: SavedFiltersUIMode): void {
    this.mode = mode;

    if (mode === 'list') {
      this.form.reset();
    }

    this.cdr.markForCheck();
  }

  saveFilter(): void {
    const { value } = this.form;

    if (!value.name) {
      return;
    }

    const filter: { name: string; scope: DatasetFilterScope } = {
      name: value.name,
      scope: value.public ? DatasetFilterScope.Account : DatasetFilterScope.Private,
    };

    this.save.emit(filter);
    this.changeMode('list');
  }

  applyFilter(filter: DatasetFilterInfo): void {
    this.apply.emit(filter);
  }

  deleteFilter(filter: DatasetFilterInfo, event: MouseEvent): void {
    event.stopPropagation();

    this.dialog
      .openConfirmDialog({
        title:
          filter.name.trim().length > 30 ? `${filter.name.trim().slice(0, 30)}...` : filter.name,
        description: 'Are you sure you want to delete this filter?',
        cancelLabel: 'Cancel',
        confirmLabel: 'Delete',
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }

        this.delete.emit(filter);
      });
  }
}
